import React from 'react'
import { Box, Skeleton, Typography } from '@mui/material'
import { Bar, Line } from 'react-chartjs-2'
import {
  allZero, axes, baseOptions, comparisonBars, comparisonLine, currentBars, currentLine, htmlTooltip, niceScale, pointLabelPlugin
} from './chartKit'
import { C, cardSx } from './tokens'
import { hourLabel, hourLong, money } from './format'
import { LegendItem, cardSubSx, cardTitleSx, tooltipDelta } from './SalesOverTime'
import Empty from './Empty'

const FULL_DAY = { Mon: 'Monday', Tue: 'Tuesday', Wed: 'Wednesday', Thu: 'Thursday', Fri: 'Friday', Sat: 'Saturday', Sun: 'Sunday' }

function Panel({ title, sub, legend, children, aria, loading, empty }) {
  return (
    <Box sx={{ ...cardSx, p: { xs: 2, md: 3 }, display: 'flex', flexDirection: 'column', minWidth: 0 }}>
      <Typography component="h2" sx={cardTitleSx}>{title}</Typography>
      <Typography sx={cardSubSx}>{sub || ' '}</Typography>
      <Box sx={{ display: 'flex', gap: 2, mt: 1.5 }} aria-hidden>{legend}</Box>
      <Box sx={{ height: 240, mt: 1.5, position: 'relative' }} role="img" aria-label={aria}>
        {loading ? <Skeleton variant="rounded" height="100%" sx={{ borderRadius: '8px' }} /> : empty ? <Empty compact message="No sales in this range." /> : children}
      </Box>
    </Box>
  )
}

export default function WeekdayHour({ data, loading }) {
  const hasPrev = !!data?.previous
  const weekday = data?.weekday || []
  const hourly = data?.hourly || []
  const first = loading && !data

  const wCur = weekday.map(w => w.sales)
  const wPrev = hasPrev ? weekday.map(w => w.prevSales) : []
  const hCur = hourly.map(h => h.sales)
  const hPrev = hasPrev ? hourly.map(h => h.prevSales) : []

  // Peak = the busiest hour by sales in the current period.
  const peak = hourly.reduce((best, h) => (h.sales > (best?.sales ?? 0) ? h : best), null)
  const peakText = peak && peak.sales > 0 ? `Peak ${hourLong(peak.hour)} · ${money(peak.sales)}` : null

  const weekData = {
    labels: weekday.map(w => w.day),
    datasets: [currentBars(wCur), ...(hasPrev ? [comparisonBars(wPrev)] : [])]
  }
  const weekOptions = {
    ...baseOptions,
    scales: axes({ money: true, scale: niceScale([wCur, wPrev]) }),
    plugins: {
      ...baseOptions.plugins,
      tooltip: htmlTooltip(i => {
        const w = weekday[i]
        if (!w) return null
        return {
          title: FULL_DAY[w.day] || w.day,
          rows: [
            { color: C.brand, square: true, label: 'This period', value: money(w.sales) },
            ...(hasPrev ? [{ color: C.prevBar, square: true, label: 'Comparison', value: money(w.prevSales) }] : [])
          ],
          delta: hasPrev ? tooltipDelta(w.sales, w.prevSales, money) : null
        }
      })
    }
  }

  const hourData = {
    labels: hourly.map(h => hourLabel(h.hour)),
    datasets: [currentLine(hCur), ...(hasPrev ? [comparisonLine(hPrev)] : [])]
  }
  const hourOptions = {
    ...baseOptions,
    // Room above the peak for its label, so the label is never cut off.
    layout: { padding: { top: 26 } },
    scales: axes({ money: true, maxXTicks: 8, scale: niceScale([hCur, hPrev]) }),
    plugins: {
      ...baseOptions.plugins,
      tooltip: htmlTooltip(i => {
        const h = hourly[i]
        if (!h) return null
        return {
          title: `${hourLong(h.hour)} – ${hourLong((h.hour + 1) % 24)}`,
          rows: [
            { color: C.brand, label: 'This period', value: money(h.sales) },
            ...(hasPrev ? [{ color: C.prev, dashed: true, label: 'Comparison', value: money(h.prevSales) }] : [])
          ],
          delta: hasPrev ? tooltipDelta(h.sales, h.prevSales, money) : null
        }
      })
    }
  }
  const peakPlugin = pointLabelPlugin(() => (peak && peakText ? { index: peak.hour, text: peakText } : null))

  return (
    <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 2 }}>
      <Panel
        title="By day of week"
        sub="Sales on each weekday"
        legend={<><LegendItem color={C.brand} square label="This period" />{hasPrev ? <LegendItem color={C.prevBar} square label="Comparison" /> : null}</>}
        loading={first}
        empty={!first && allZero(wCur, wPrev)}
        aria={`Sales by day of week: ${weekday.map(w => `${w.day} ${money(w.sales)}`).join(', ')}`}>
        <Bar data={weekData} options={weekOptions} />
      </Panel>
      <Panel
        title="By hour of day"
        sub={peakText || 'Sales across the day'}
        legend={<><LegendItem color={C.brand} label="This period" />{hasPrev ? <LegendItem color={C.prev} dashed label="Comparison" /> : null}</>}
        loading={first}
        empty={!first && allZero(hCur, hPrev)}
        aria={peak ? `Sales by hour of day. ${peakText || ''}` : 'Sales by hour of day'}>
        <Line data={hourData} options={hourOptions} plugins={[peakPlugin]} />
      </Panel>
    </Box>
  )
}
